import { Component, OnInit, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, tap } from 'rxjs';
import {
  GroupType,
  GamesGroupService,
  ExternalPredictorGroupGames,
  ExternalTriviaGroupGames,
  ExternalPollGroupGames,
  UnauthorizedEntityService,
  UnauthorizedEntityModel,
  AuthService,
} from '@fgb/core';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'fgb-outside-portal-game-page',
  templateUrl: './outside-portal-game-page.component.html',
  styleUrls: ['./outside-portal-game-page.component.scss'],
})
export class OutsidePortalGamePageComponent implements OnInit, AfterViewInit {
  @ViewChild('tabNavigation', { static: false }) tabNavigation!: ElementRef;
  public activeTab: number = 0;
  public readonly tabTypes = 0;
  public readonly groupTypes = GroupType;
  groupId: number = 0;
  groupType: GroupType = GroupType.Trivia;
  externalUrl: string = '';
  gameDetails: ExternalTriviaGroupGames | ExternalPollGroupGames | ExternalPredictorGroupGames;
  unauthorizedEntity: UnauthorizedEntityModel | null = null;
  unauthorizedEntityCreated: boolean = false;
  unauthorizedForm: FormGroup;
  isFormSubmitted: boolean = false;
  isFormSubmitting: boolean = false;
  predictorSubmitted: boolean = false;
  isLoading: boolean = true;

  constructor(
    private gamesGroupService: GamesGroupService,
    private unauthorizedEntityService: UnauthorizedEntityService,
    private authService: AuthService,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.externalUrl = this.activeRoute.snapshot.paramMap.get('externalUrl') || '';
    this.unauthorizedForm = this.fb.group({
      firstName: ['', [Validators.required]],
      surname: ['', [Validators.required]],
      emailAddress: ['', [Validators.required, Validators.email]],
    });
    this.gamesGroupService
      .getGroupDetailsByExternalUrl(this.externalUrl)
      .pipe(
        tap((res) => {
          if (res) {
            this.groupId = res.GroupId;
            this.groupType = res.GroupType;
          }
        })
      )
      .subscribe({
        next: () => {
          if(this.authService.isAuthenticated()){
            sessionStorage.removeItem('UnauthorizedEntityGuid');
            this.router.navigateByUrl(`/games-v2/${GroupType[this.groupType].toLocaleLowerCase()}`)
          }else{
            this.fetchGameDetails();
          }
        },
        error: (error) => {},
      });
    this.getUnauthorizedEntity();
  }

  ngAfterViewInit(): void {
    // Remove touch event handling - let native scrolling work
  }

  /**
   * Sets the active tab
   * @param tab - The tab to set as active
   */
  public setActiveTab(tab: number): void {
    this.activeTab = tab;
  }

  fetchGameDetails() {
    let gameRequest: Observable<ExternalTriviaGroupGames | ExternalPollGroupGames | ExternalPredictorGroupGames>;
    switch (this.groupType) {
      case GroupType.Trivia:
        gameRequest = this.gamesGroupService.fetchUnauthorizedTrivaGame(this.groupId);
        break;
      case GroupType.Poll:
        gameRequest = this.gamesGroupService.fetchUnauthorizedPollGame(this.groupId);
        break;
      case GroupType.Predictor:
        gameRequest = this.gamesGroupService.fetchPredictorGroupGames(
          this.groupId,
          true
        ) as Observable<ExternalPredictorGroupGames>;
    }

    gameRequest
      .pipe(
        tap((games) => {
          this.gameDetails = games;
          if (this.groupType == GroupType.Trivia) {
            (this.gameDetails as ExternalTriviaGroupGames).Completed = false;
          }
        })
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
        },
      });
  }

  /**
   * Determines which template to show based on submission mode
   */
  submitUnauthorizedForm() {
    if (!this.unauthorizedForm) {
      return;
    }
    if (this.unauthorizedForm.invalid) {
      this.unauthorizedForm.markAllAsTouched();
      return;
    }

    this.isFormSubmitting = true;
    this.unauthorizedEntityService.updateUnauthorizedEntity(this.unauthorizedForm.value as UnauthorizedEntityModel).subscribe({
      next: () => {
        this.isFormSubmitted = true;
        this.isFormSubmitting = false;
      },
      error: (error) => {
        this.isFormSubmitting = false;
      },
    });
  }

  checkGameSubmitted(): boolean {
    switch (this.groupType) {
      case GroupType.Trivia:
        return (this.gameDetails as ExternalTriviaGroupGames).EntityGroupCompleted;
      case GroupType.Poll:
        return (this.gameDetails as ExternalPollGroupGames).Completed;
      case GroupType.Predictor:
        return this.predictorSubmitted;
    }
  }

  getUnauthorizedEntity() {
    this.unauthorizedEntityService.getUnauthorizedEntity().subscribe((entity) => {
      this.unauthorizedEntity = entity;
      this.unauthorizedEntityCreated = entity && !!entity.EmailAddress;
    });
  }

  externalPredictorSubmitted(submitted: boolean) {
    this.predictorSubmitted = submitted;
    this.getUnauthorizedEntity();
    this.fetchGameDetails();
  }

  isGameCompleted(completed: boolean) {
    if (this.groupType == GroupType.Trivia) {
      (this.gameDetails as ExternalTriviaGroupGames).Completed = completed;
      (this.gameDetails as ExternalTriviaGroupGames).EntityGroupCompleted = completed;
    }
  }

  /**
   * Resets the form and success state (useful for testing or allowing resubmission)
   */
  resetForm() {
    this.isFormSubmitted = false;
    this.isFormSubmitting = false;
    this.unauthorizedForm.reset();
  }
}
